import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useDropzone, FileRejection } from 'react-dropzone';

import { snackbar } from '@occc-app/design-system';
import { KioskImageAspectRatio } from '@occc/graphql';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { AssetImageUploadContent } from './asset-image-upload-content';

import { DevicePreviewFrame } from '../../assets-details-page/components/device-preview-frame';
import { ipadBackground, androidBackground } from '../../assets-details-page/images';
import { useAssetUpload } from '../../../hooks/use-asset-upload.hook';
import { MAX_FILE_SIZE_MB, type AspectRatio } from '../../../touchpoint.constants';
import { validateImageFile } from '../asset-create.validation';

type AssetImageUploadProps = {
  aspectRatio: AspectRatio;
  fieldName: string;
  value: File | null;
  onChange: (file: File | null) => void;
  urlValue?: string | null;
  onUrlChange?: (url: string | null) => void;
  error?: string;
  required?: boolean;
  label?: string;
};

const aspectRatioToEnum: Record<AspectRatio, KioskImageAspectRatio> = {
  '4x3': KioskImageAspectRatio.Landscape,
  '1x1': KioskImageAspectRatio.Square,
  '9x16': KioskImageAspectRatio.Portrait,
};

const imageStyles: Record<AspectRatio, React.CSSProperties> = {
  '4x3': {
    width: '377px',
    height: '263px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '9px',
  },
  '1x1': {
    width: '377.39px',
    height: '263.58px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '9px',
  },
  '9x16': {
    width: '154px',
    height: '247px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '4px',
  },
};

const backgroundImages: Record<AspectRatio, string> = {
  '4x3': ipadBackground,
  '1x1': ipadBackground,
  '9x16': androidBackground,
};

export const AssetImageUpload = ({
  aspectRatio,
  fieldName,
  value,
  onChange,
  urlValue,
  onUrlChange,
  error,
  required = false,
  label,
}: AssetImageUploadProps) => {
  const { isEditable } = useCanEditFormFieldsContext();
  const [previewUrl, setPreviewUrl] = useState<string | null>(urlValue || null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const { uploadFile, resetUploadState, uploadedFileState } = useAssetUpload({
    aspectRatio: aspectRatioToEnum[aspectRatio],
  });

  const isUploading = uploadedFileState !== null && uploadedFileState.url === undefined;

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Cleanup blob URL when previewUrl changes from blob to URL
  useEffect(() => {
    if (previewUrl && !previewUrl.startsWith('blob:') && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [previewUrl]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      const validationError = validateImageFile(file);
      if (validationError) {
        setUploadError(validationError);
        onChange(null);
        // Show toast for unsupported file types
        if (validationError.includes('PNG or JPEG')) {
          snackbar.error('Unsupported file type. Please use PNG or JPEG images.');
        }
        return;
      }

      setUploadError(null);
      onChange(file);

      // Clean up previous blob URL if exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      // Create preview URL
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      setPreviewUrl(url);

      // Upload file to get URL
      const uploadedUrl = await uploadFile(file);

      if (uploadedUrl) {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setPreviewUrl(uploadedUrl);
        onUrlChange?.(uploadedUrl);
      } else {
        // Upload failed, reset
        onChange(null);
        setPreviewUrl(null);
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
      }
    },
    [onChange, uploadFile, onUrlChange],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileSelect(acceptedFiles[0]);
      }
    },
    [handleFileSelect],
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      const errors = rejection.errors.map((error) => error.code);
      const hasFileTypeError = errors.includes('file-invalid-type');
      const hasFileSizeError = errors.includes('file-too-large');

      if (hasFileTypeError) {
        snackbar.error('Unsupported file type. Please use PNG or JPEG images.');
      } else if (hasFileSizeError) {
        snackbar.error(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      } else {
        // Fallback: show error for any other rejection
        snackbar.error('File upload failed. Please check the file type and size.');
      }
    }
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    disabled: !isEditable,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024,
  });

  const handleRemove = useCallback(() => {
    onChange(null);
    setPreviewUrl(null);
    setUploadError(null);
    onUrlChange?.(null);
    resetUploadState();
    // Clean up blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [onChange, resetUploadState, onUrlChange]);

  const backgroundImage = backgroundImages[aspectRatio];
  const style = imageStyles[aspectRatio];

  return (
    <Box>
      <Box sx={{ py: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          {label || `${aspectRatio} image`}
        </Typography>
      </Box>
      <DevicePreviewFrame aspectRatio={aspectRatio} backgroundImage={backgroundImage}>
        <Box {...getRootProps()}>
          <AssetImageUploadContent
            previewUrl={previewUrl}
            aspectRatio={aspectRatio}
            style={style}
            onRemove={handleRemove}
            isDragActive={isDragActive}
            onOpen={open}
            inputProps={getInputProps()}
            fieldName={fieldName}
            isUploading={isUploading}
            disabled={!isEditable}
          />
        </Box>
      </DevicePreviewFrame>
      {(error || uploadError) && (
        <Typography variant='caption' color='error' sx={{ display: 'block', mt: 0.5 }}>
          {error || uploadError}
        </Typography>
      )}
    </Box>
  );
};
